import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  getBrandRequests, 
  approveBrandRequest, 
  rejectBrandRequest,
  getBrandRequestStatistics 
} from '../../services/brand';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Paper,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Badge
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Add, 
  Edit, 
  Delete, 
  Business, 
  Refresh, 
  CheckCircle,
  Cancel,
  Visibility,
  Warning,
  ChevronLeft,
  ChevronRight,
  Image,
  Public,
  Star,
  StarBorder,
  RequestPage,
  Person,
  Store
} from '@mui/icons-material';

const BrandRequests = () => {
  // Local state management
  const [brandRequests, setBrandRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalRequests, setTotalRequests] = useState(0);
  const [statistics, setStatistics] = useState({});
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [logoPreviewOpen, setLogoPreviewOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Calculate total pages
  const totalPages = Math.ceil(totalRequests / rowsPerPage) || 1;

  // Fetch brand requests
  const fetchBrandRequestsData = useCallback(async (pageArg = page, rowsArg = rowsPerPage, filters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pageArg + 1,
        page_size: rowsArg,
        status: filters.status !== 'all' ? filters.status : undefined,
        search: filters.search || undefined,
      };
      
      // Remove undefined values
      Object.keys(params).forEach(key => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });
      
      // Filter out approved requests - only show pending and rejected
      const filteredParams = {
        ...params,
        status: 'pending,rejected'  // Only show pending and rejected requests
      };
      
      const response = await getBrandRequests(filteredParams);
      
      if (response.success) {
        setBrandRequests(response.data || []);
        setTotalRequests(response.count || 0);
      } else {
        setError(response.error || 'Failed to fetch brand requests');
        setBrandRequests([]);
        setTotalRequests(0);
      }
    } catch (err) {
      console.error('Error fetching brand requests:', err);
      setError(err.message || 'Failed to fetch brand requests');
      setBrandRequests([]);
      setTotalRequests(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  // Load data on component mount and when dependencies change
  useEffect(() => {
    fetchBrandRequestsData(page, rowsPerPage, { status: statusFilter, search: searchTerm });
  }, [fetchBrandRequestsData, page, rowsPerPage, statusFilter, searchTerm]);

  // DataGrid columns
  const columns = useMemo(() => [
    {
      field: 'id',
      headerName: '#',
      width: 60,
      renderCell: (params) => (
        <Typography variant="body2">
          {page * rowsPerPage + params.api.getRowIndexRelativeToVisibleRows(params.id) + 1}
        </Typography>
      ),
    },
    {
      field: 'logo',
      headerName: 'Logo',
      width: 80,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {params.value ? (
            <Tooltip title="Click to preview logo">
              <Avatar
                src={params.value}
                alt={`${params.row.brand_name} logo`}
                sx={{ 
                  width: 40, 
                  height: 40, 
                  border: '1px solid', 
                  borderColor: 'divider',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }
                }}
                variant="rounded"
                onClick={() => handleLogoPreview(params.row)}
              />
            </Tooltip>
          ) : (
            <Avatar
              sx={{ width: 40, height: 40, bgcolor: 'grey.300', border: '1px solid', borderColor: 'divider' }}
              variant="rounded"
            >
              <Image />
            </Avatar>
          )}
        </Box>
      ),
    },
    {
      field: 'brand_name',
      headerName: 'Brand Name',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight="medium">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'requested_by_full_name',
      headerName: 'Requested By',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
            {params.row.requested_by_username?.[0] || '?'}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.value || params.row.requested_by_username || 'Unknown'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'merchant_business_name',
      headerName: 'Merchant',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value || 'N/A'} 
          size="small" 
          variant="outlined"
          icon={<Public />}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const getStatusColor = (status) => {
          switch (status) {
            case 'pending': return 'warning';
            case 'approved': return 'success';
            case 'rejected': return 'error';
            default: return 'default';
          }
        };
        
        const getStatusIcon = (status) => {
          switch (status) {
            case 'pending': return <Warning />;
            case 'approved': return <CheckCircle />;
            case 'rejected': return <Cancel />;
            default: return <RequestPage />;
          }
        };
        
        return (
          <Chip 
            label={params.value?.charAt(0).toUpperCase() + params.value?.slice(1)}
            color={getStatusColor(params.value)}
            size="small"
            icon={getStatusIcon(params.value)}
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Requested',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {params.row.status === 'pending' && (
            <>
              <Tooltip title="Approve Request">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleApproveRequest(params.row.id)}
                >
                  <CheckCircle />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject Request">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRejectRequest(params.row)}
                >
                  <Cancel />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip title="View Details">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleViewDetails(params.row)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'categories',
      headerName: 'Categories',
      width: 200,
      renderCell: (params) => {
        const categories = params.value || [];
        const categoryNames = categories.map(cat => cat.name).join(', ');
        
        return (
          <Box sx={{ maxWidth: 180 }}>
            {categoryNames ? (
              <Tooltip title={categoryNames}>
                <Typography variant="body2" noWrap>
                  {categoryNames}
                </Typography>
              </Tooltip>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No categories
              </Typography>
            )}
          </Box>
        );
      }
    },
  ], [page, rowsPerPage]);

  // Filtered data
  const filteredRequests = useMemo(() => {
    return brandRequests.filter(request => {
      const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
      const matchesSearch = !searchTerm || 
        request.brand_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.merchant_business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.requested_by_username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.requested_by_full_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesStatus && matchesSearch;
    });
  }, [brandRequests, statusFilter, searchTerm]);

  // Event handlers
  const handleApproveRequest = async (requestId) => {
    setLoading(true);
    
    try {
      await approveBrandRequest(requestId);
      
      setSnackbar({
        open: true,
        message: 'Brand request approved successfully!',
        severity: 'success'
      });
      
      // Refresh the data
      fetchBrandRequestsData(page, rowsPerPage, { status: statusFilter, search: searchTerm });
    } catch (error) {
      console.error('Error approving request:', error);
      setSnackbar({
        open: true,
        message: `Error approving request: ${error.message || error}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setRejectModalOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!selectedRequest || !rejectionReason.trim()) return;
    
    setLoading(true);
    
    try {
      await rejectBrandRequest(selectedRequest.id, rejectionReason);
      
      setRejectModalOpen(false);
      setSelectedRequest(null);
      setRejectionReason('');
      
      setSnackbar({
        open: true,
        message: 'Brand request rejected successfully!',
        severity: 'success'
      });
      
      // Refresh the data
      fetchBrandRequestsData(page, rowsPerPage, { status: statusFilter, search: searchTerm });
    } catch (error) {
      console.error('Error rejecting request:', error);
      setSnackbar({
        open: true,
        message: `Error rejecting request: ${error.message || error}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setViewModalOpen(true);
  };

  const handleLogoPreview = (request) => {
    setSelectedRequest(request);
    setLogoPreviewOpen(true);
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (event) => {
    const value = event.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
    } else {
      setRowsPerPage(parseInt(value, 10));
      setPage(0);
      setCustomRowsActive(false);
    }
  };

  const handleCustomRowsInputChange = (event) => {
    setCustomRowsInput(event.target.value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput, 10);
    if (value && value > 0) {
      setRowsPerPage(value);
      setPage(0);
    }
    setCustomRowsActive(false);
    setCustomRowsInput('');
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Brand Requests Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage brand creation requests from merchants
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Search Requests"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by brand name or merchant..."
              InputProps={{
                startAdornment: <RequestPage sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => fetchBrandRequestsData(page, rowsPerPage, { status: statusFilter, search: searchTerm })}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#a8a8a8',
          }
        }}>
          {filteredRequests.length === 0 && !loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', py: 4 }}>
              <RequestPage sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                No brand requests found
              </Typography>
              <Button variant="outlined" onClick={() => fetchBrandRequestsData(page, rowsPerPage, { status: statusFilter, search: searchTerm })}>
                Refresh Data
              </Button>
            </Box>
          ) : (
            <DataGrid
              rows={filteredRequests}
              columns={columns}
              loading={loading}
              pageSize={rowsPerPage}
              rowsPerPageOptions={[]}
              disableSelectionOnClick
              hideFooter
              getRowId={(row) => row.id}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme => theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '8px 4px', sm: '8px 12px' }
                }
              }}
            />
          )}
        </Box>

        {/* Custom Pagination */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: { xs: 'center', sm: 'flex-end' }, 
          mt: 3, 
          gap: 2,
          flexWrap: 'wrap'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={handlePrevPage} disabled={page === 0}>
              <ChevronLeft />
            </IconButton>
            <Typography variant="body2">
              Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
            </Typography>
            <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
              <ChevronRight />
            </IconButton>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              Rows per page:
            </Typography>
            {!customRowsActive ? (
              <TextField
                select
                size="small"
                value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
                onChange={handleRowsPerPageChange}
                sx={{ width: 100 }}
              >
                {[10, 20, 50, 100].map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
                <MenuItem value="custom">Custom</MenuItem>
              </TextField>
            ) : (
              <TextField
                size="small"
                value={customRowsInput}
                onChange={handleCustomRowsInputChange}
                onBlur={handleCustomRowsInputBlur}
                autoFocus
                sx={{ width: 100 }}
                inputProps={{ min: 1, style: { textAlign: 'center' } }}
                placeholder="Rows"
              />
            )}
          </Box>
        </Box>
      </Paper>

      {/* Reject Request Modal */}
      <Dialog
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Brand Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Rejecting brand request: <strong>{selectedRequest?.brand_name}</strong>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Please provide a reason for rejecting this brand request..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectModalOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmReject}
            variant="contained"
            color="error"
            disabled={!rejectionReason.trim()}
          >
            Reject Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logo Preview Modal */}
      <Dialog
        open={logoPreviewOpen}
        onClose={() => setLogoPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Business color="primary" />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {selectedRequest?.brand_name} - Logo Preview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Verify logo authenticity and quality
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: 3,
            py: 2
          }}>
            {/* Large Logo Preview */}
            <Box sx={{ 
              position: 'relative',
              border: '2px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              bgcolor: 'grey.50',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              <Avatar
                src={selectedRequest?.logo}
                alt={`${selectedRequest?.brand_name} logo`}
                sx={{ 
                  width: 200, 
                  height: 200,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
                variant="rounded"
              />
            </Box>

            {/* Logo Information */}
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {selectedRequest?.brand_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Logo URL: 
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  wordBreak: 'break-all',
                  bgcolor: 'grey.100',
                  p: 1,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem'
                }}
              >
                {selectedRequest?.logo}
              </Typography>
            </Box>

            {/* Verification Guidelines */}
            <Paper sx={{ p: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200', width: '100%' }}>
              <Typography variant="subtitle2" color="info.800" fontWeight="bold" gutterBottom>
                🔍 Logo Verification Checklist:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2, color: 'info.700' }}>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  Is the logo clear and high quality?
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  Does it match the brand name and description?
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  Is it appropriate and professional?
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  Are there any copyright or trademark concerns?
                </Typography>
                <Typography component="li" variant="body2">
                  Does it represent the brand accurately?
                </Typography>
              </Box>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoPreviewOpen(false)}>
            Close
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => {
              setLogoPreviewOpen(false);
              // Optionally open the full request details
              setViewModalOpen(true);
            }}
          >
            View Full Details
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedRequest?.logo && (
              <Avatar
                src={selectedRequest.logo}
                alt={`${selectedRequest?.brand_name} logo`}
                sx={{ width: 48, height: 48, border: '2px solid', borderColor: 'divider' }}
                variant="rounded"
              />
            )}
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {selectedRequest?.brand_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Brand Request Details
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business color="primary" />
                Basic Information
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Brand Name
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedRequest?.brand_name || 'N/A'}
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Country
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedRequest?.country || 'N/A'}
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Description
              </Typography>
              <Typography variant="body1" sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, minHeight: 60 }}>
                {selectedRequest?.description || 'No description provided'}
              </Typography>
            </Grid>

            {/* Logo */}
            {selectedRequest?.logo && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Brand Logo
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Tooltip title="Click to preview in full size">
                    <Avatar
                      src={selectedRequest.logo}
                      alt={`${selectedRequest?.brand_name} logo`}
                      sx={{ 
                        width: 120, 
                        height: 120, 
                        border: '2px solid', 
                        borderColor: 'divider',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                        }
                      }}
                      variant="rounded"
                      onClick={() => {
                        setViewModalOpen(false);
                        setLogoPreviewOpen(true);
                      }}
                    />
                  </Tooltip>
                </Box>
              </Grid>
            )}

            {/* Request Information */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <RequestPage color="primary" />
                Request Information
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <Chip 
                label={selectedRequest?.status_display || 'Unknown'}
                color={
                  selectedRequest?.status === 'pending' ? 'warning' :
                  selectedRequest?.status === 'approved' ? 'success' :
                  selectedRequest?.status === 'rejected' ? 'error' : 'default'
                }
                size="small"
                icon={
                  selectedRequest?.status === 'pending' ? <Warning /> :
                  selectedRequest?.status === 'approved' ? <CheckCircle /> :
                  selectedRequest?.status === 'rejected' ? <Cancel /> : <RequestPage />
                }
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Request Date
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedRequest?.created_at ? new Date(selectedRequest.created_at).toLocaleString() : 'N/A'}
              </Typography>
            </Grid>

            {/* Requester Information */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <Star color="primary" />
                Requester Information
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Requested By
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                  {selectedRequest?.requested_by_username?.[0] || '?'}
                </Avatar>
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedRequest?.requested_by_full_name || 'Unknown User'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedRequest?.requested_by_username}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Merchant Business
              </Typography>
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {selectedRequest?.merchant_business_name || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedRequest?.merchant_email || 'No email'}
                </Typography>
              </Box>
            </Grid>

            {/* Rejection Information */}
            {selectedRequest?.status === 'rejected' && selectedRequest?.rejection_reason && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <Cancel color="error" />
                  Rejection Information
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Rejection Reason
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    bgcolor: 'error.50', 
                    p: 2, 
                    borderRadius: 1, 
                    border: '1px solid',
                    borderColor: 'error.200',
                    color: 'error.800'
                  }}
                >
                  {selectedRequest.rejection_reason}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BrandRequests;
